/**
 * THE POLICY LIBRARY.
 *
 * One page, shaped by who is reading it:
 *
 *   a reader       search, browse by department or category, and the
 *                  published policies. Nothing about a policy's upkeep.
 *   people who     the same, with the tabs for work in progress, an Owner
 *   work on        column and filter, "Mine", and where each policy stands.
 *   policies
 *   administrators all of that, under one compact band of what needs
 *                  attention.
 *
 * NOTHING IS "LOADED". The page asks the server for the page it is showing,
 * in the order it is showing it, and for nothing else; a filter, a tab, a
 * sort and "Next" are each a new question. It behaves the same for five
 * hundred policies and for twenty thousand. Typing sends the same question to
 * the search index instead, and the reader is not asked to know which
 * answered.
 *
 * This file only arranges. Each concern is its own module: what is asked
 * (libraryQuery), the answer (useLibrary, libraryPage), and the search box,
 * tabs and filters, browse panel, attention band, table and pager.
 */
import { Link } from 'react-router-dom';
import { Spinner } from '../../../app/Spinner';
import { TabButtons } from '../../../shared/Tabs';
import { useTaxonomy } from '../../admin/useTaxonomy';
import { PolicyLayoutChoice, usePolicyLayout } from '../policyLayout';
import { AttentionSummary } from './AttentionSummary';
import { BrowsePanel } from './BrowsePanel';
import { LibraryFilters } from './LibraryFilters';
import { LibraryPager, rangeText } from './LibraryPager';
import { LibrarySearchBox } from './LibrarySearchBox';
import { LibraryTable } from './LibraryTable';
import { columnsFor } from './libraryColumns';
import { defaultView, isNamedSet, viewsFor, withoutFilters, type LibraryView } from './libraryQuery';
import { ATTENTION_VIEWS, emptyState, TAB_VIEWS, VIEW_LABEL, VIEW_MEANING } from './libraryText';
import { useAttentionCounts, useFormerOwners, useLibraryRoute, useOwnerDirectory } from './useLibrary';
import styles from './Library.module.css';


export function PoliciesPage() {
  const { who, state, data, now, search, linkState, change, next, previous } = useLibraryRoute();
  const [layout, setLayout] = usePolicyLayout();

  const departments = useTaxonomy('department', true);
  const categories = useTaxonomy('category', true);
  const documentTypes = useTaxonomy('documentType', true);
  const owners = useOwnerDirectory(who.ownersShown);
  const counts = useAttentionCounts(who, now, who.isAdmin);
  const formerOwners = useFormerOwners(who, state.view === 'ownerInactive');

  const views = viewsFor(who);
  const tabs = TAB_VIEWS.filter((v) => views.includes(v));
  const isCondition = !TAB_VIEWS.includes(state.view);
  const named = isNamedSet(state);
  const searching = data.mode === 'search' || data.mode === 'unsupported';
  const columns = columnsFor(who);
  const options = {
    departments: departments.data ?? [], categories: categories.data ?? [],
    documentTypes: documentTypes.data ?? [], owners: owners.data ?? [],
  };
  const nameOf = (items: { id: string; name: string }[], id: string | null) => items.find((i) => i.id === id)?.name ?? null;

  const open = (view: LibraryView) => change({ ...withoutFilters(state), view, sort: 'title', dir: 'asc', ids: [] });
  const empty = emptyState(state, {
    searching: data.mode === 'search',
    departmentName: nameOf(options.departments, state.department),
    categoryName: nameOf(options.categories, state.category),
    mayUpload: who.mayUpload,
    narrowed: state.view !== defaultView(who),
  });
  // Above the table as well as below it: how many, and that there is more, without scrolling to the end.
  const range = data.rows.length > 0 && !data.isUpdating && data.mode !== 'named' ? `${rangeText(data)}${data.total !== null ? (data.total === 1 ? ' policy' : ' policies') : ''}` : null;
  const caption = `${named ? 'Just uploaded' : VIEW_LABEL[state.view]}${data.mode === 'search' ? `, matching ${state.text.trim()}` : ''}`;

  const list = (
    <>
      {data.mode === 'unsupported' ? (
        <div className={styles.notice} role="status">
          <p>{data.unsupported}</p>
          <p className={styles.emptyAction}><button type="button" className={styles.linkBtn} onClick={() => change({ text: '' })}>Clear the search</button></p>
        </div>
      ) : data.error ? (
        // A list that failed is not an empty list, and is never shown as one.
        <div className={styles.error} role="alert">
          <p>
            <strong>{data.mode === 'search' ? 'The search could not be run.' : 'These policies could not be shown.'}</strong>{' '}
            Nothing is listed below, and that does not mean there is nothing. Try again; if it keeps happening, tell your administrator.
          </p>
          <p className={styles.emptyAction}><button type="button" className={styles.linkBtn} onClick={data.retry}>Try again</button></p>
        </div>
      ) : data.isLoading ? (
        <Spinner label="Loading policies" />
      ) : data.rows.length === 0 ? (
        <div className={`${styles.tableWrap} ${styles.empty}`} role="status">
          <p className={styles.emptyTitle}>{empty.title}</p>
          {empty.hint && <p className={styles.emptyHint}>{empty.hint}</p>}
          {empty.action === 'clearSearch' && <p className={styles.emptyAction}><button type="button" className={styles.linkBtn} onClick={() => change({ text: '' })}>Clear the search</button></p>}
          {empty.action === 'searchEverything' && <p className={styles.emptyAction}><button type="button" className={styles.linkBtn} onClick={() => change({ ...withoutFilters(state), view: defaultView(who) })}>Search all policies</button></p>}
          {empty.action === 'upload' && <p className={styles.emptyAction}><Link to="/policies/upload" className={styles.linkBtn}>Upload a policy</Link></p>}
        </div>
      ) : (
        <>
          <LibraryTable columns={columns} rows={data.rows} now={now} search={search} linkState={linkState} updating={data.isUpdating} caption={caption} />
          <LibraryPager data={data} size={state.size} onSize={(size) => change({ size })} onPrevious={previous} onNext={next} />
        </>
      )}
    </>
  );

  return (
    <div className={styles.page}>
      <div className={styles.head}>
        <h1 className={styles.title}>Policies</h1>
        <PolicyLayoutChoice value={layout} onChange={setLayout} idPrefix="library-layout" />
      </div>

      <LibrarySearchBox value={state.text} onChange={(text) => change({ text, ids: [] }, { replace: true })} />

      {who.isAdmin && !named && (
        <AttentionSummary views={views.filter((v) => ATTENTION_VIEWS.includes(v))} counts={counts.data} loading={counts.isLoading} current={state.view} onOpen={open} />
      )}

      {named ? (
        <div className={styles.condition}>
          <span className={styles.chip}>
            Just uploaded ({state.ids.length})
            <button type="button" className={styles.linkBtn} onClick={() => change({ ids: [] })}>Back to all policies</button>
          </span>
        </div>
      ) : (
        <>
          {tabs.length > 1 && (
            <div className={styles.bar}>
              <TabButtons
                label="Policies by status"
                items={tabs.map((v) => ({ key: v, label: VIEW_LABEL[v] }))}
                // A condition from Needs attention is not one of the tabs, so none is filled.
                current={(isCondition ? '' : state.view) as LibraryView}
                onSelect={open}
              />
              {/* How many, and that there is more, without scrolling to the end; on the tabs' own line so it costs the table no height. */}
              {range && <span className={styles.barRange} aria-hidden="true">{range}</span>}
            </div>
          )}
          {isCondition && (
            <div className={styles.condition}>
              <h2 className={styles.conditionTitle}>
                {VIEW_LABEL[state.view]}
                <button type="button" className={styles.linkBtn} style={{ fontSize: 'var(--fs-sm)' }} onClick={() => open(defaultView(who))}>Back to all policies</button>
              </h2>
              {VIEW_MEANING[state.view] && <p className={styles.conditionMeaning}>{VIEW_MEANING[state.view]}</p>}
            </div>
          )}
        </>
      )}

      {who.ownersShown ? (
        <>
          {!named && <LibraryFilters who={who} state={state} options={options} searching={searching} onChange={change} formerOwners={formerOwners.data ?? []} range={tabs.length > 1 ? null : range} />}
          {list}
        </>
      ) : (
        // A reader: browse beside the list, and only the sort above it.
        <div className={styles.withBrowse}>
          <BrowsePanel
            departments={options.departments} categories={options.categories}
            department={state.department} category={state.category}
            onChange={(patch) => change(patch)}
          />
          <div>
            {!named && <LibraryFilters who={who} state={state} options={options} searching={searching} onChange={change} hideTaxonomy range={tabs.length > 1 ? null : range} />}
            {list}
          </div>
        </div>
      )}
    </div>
  );
}
